import React, { useState } from 'react';
import { NamedLink, CategoryGrid, BrandGrid, Reveal } from '../../components';

import heroBg from '../../assets/hero-bg.jpg';
import trustpilotStars from '../../assets/trustpilot-stars.svg';
import trustpilotLogoLight from '../../assets/trustpilot-logo.svg';
import videoThumb from '../../assets/video-thumb.jpg';

import { ProductEducation, HowItWorks } from './WheelplaceLanding';

import css from './WheelplaceLandingMobile.module.css';

/* -------------------------- Hero (Köpa / Sälja cards) -------------------------- */

const MobileHero = () => (
  <section className={css.hero}>
    <img src={heroBg} alt="" className={css.heroBg} />
    <div className={css.heroOverlay} />
    <div className={css.heroInner}>
      <h1 className={css.heroTitle}>
        Köp och sälj begagnade eller
        <br />
        nya hjul, däck och fälgar
      </h1>
      <p className={css.heroSub}>
        Sveriges smartaste marknadsplats för hjul. Sök med registreringsnummer — vi hjälper dig hitta rätt.
      </p>

      <div className={css.heroCards}>
        <NamedLink name="SearchPage" className={css.heroCard}>
          <span className={css.heroCardIcon}>
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"
              />
            </svg>
          </span>
          <span className={css.heroCardBody}>
            <span className={css.heroCardTitle}>Köpa</span>
            <span className={css.heroCardDesc}>Hitta hjul som passar din bil.</span>
          </span>
          <svg
            className={css.heroCardArrow}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M9 5l7 7-7 7" />
          </svg>
        </NamedLink>

        <NamedLink name="WheelplaceCreateListingPage" className={css.heroCard}>
          <span className={css.heroCardIcon}>
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z"
              />
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
            </svg>
          </span>
          <span className={css.heroCardBody}>
            <span className={css.heroCardTitle}>Sälja</span>
            <span className={css.heroCardDesc}>Skapa en annons på under 2 minuter.</span>
          </span>
          <svg
            className={css.heroCardArrow}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M9 5l7 7-7 7" />
          </svg>
        </NamedLink>
      </div>

      <a
        className={css.trust}
        href="https://www.trustpilot.com/review/www.wheelplace.com"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Läs omdömen om Wheelplace på Trustpilot"
      >
        <img src={trustpilotLogoLight} alt="Trustpilot" className={css.trustLogo} />
        <img src={trustpilotStars} alt="" className={css.trustStars} />
        <span className={css.trustCopy}>4.3 · 39 omdömen</span>
      </a>
    </div>
  </section>
);

/* -------------------------- Featured listings --------------------------
 * MobileFeatured is a passthrough — FeaturedListings renders its own
 * title + prev/next scroll buttons + "Visa alla annonser" link, and its
 * internal CSS already has mobile overrides (font-size, snap scroller,
 * 75vw cards). Our mobile.css just re-enables the scroll arrows that the
 * desktop mobile-breakpoint disabled, and zeros out the duplicate section
 * padding. */

const MobileFeatured = ({ FeaturedListingsComponent }) =>
  FeaturedListingsComponent ? (
    <div className={css.featuredScrollerWrap}>
      <FeaturedListingsComponent />
    </div>
  ) : null;

/* -------------------------- Categories -------------------------- */

const MobileCategories = () => (
  <section className={css.categories}>
    <div className={css.sectionPad}>
      <span className={css.eyebrow}>Kategorier</span>
      <h2 className={css.sectionHeading}>Bläddra efter typ</h2>
    </div>
    <CategoryGrid />
  </section>
);

/* -------------------------- Brands -------------------------- */

const MobileBrands = () => (
  <section className={css.brands}>
    <div className={css.sectionPad}>
      <span className={css.eyebrow}>Bilmärken</span>
      <h2 className={css.sectionHeading}>Efter bilmärke</h2>
    </div>
    <BrandGrid />
  </section>
);

/* -------------------------- Trust band (Hållbart val + video) -------------------------- */

const MobileTrust = () => {
  const [playing, setPlaying] = useState(false);
  return (
    <section className={css.trustBand}>
      <div className={css.sectionPad}>
        <span className={css.trustEyebrow}>Hållbart val</span>
        <h2 className={css.trustTitle}>Varje hjul förtjänar en andra chans</h2>
        <p className={css.trustCopy2}>
          Över 12&nbsp;450 hjul har hittat nya ägare via Wheelplace. Mindre avfall, sparade resurser, samma trygghet.
        </p>
        <div className={css.videoFrame}>
          {playing ? (
            <iframe
              src="https://www.youtube.com/embed/X6x1yPQ7n6A?autoplay=1"
              title="Wheelplace — Hållbarhet"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className={css.videoIframe}
            />
          ) : (
            <button
              type="button"
              className={css.videoThumbButton}
              onClick={() => setPlaying(true)}
              aria-label="Spela video"
            >
              <img src={videoThumb} alt="" className={css.videoThumbImg} />
              <span className={css.videoPlay}>
                <svg fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </span>
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

/* -------------------------- FAQ teaser + contact -------------------------- */

// Keep in sync with the desktop FAQS in WheelplaceLanding.js.
const FAQS = [
  {
    q: 'Hur fungerar det att köpa hjul på Wheelplace?',
    a: 'Sök bland annonser eller skriv in ditt regnummer. Kontakta säljaren direkt för frågor och betalning.',
  },
  {
    q: 'Hur lägger jag upp en annons?',
    a: 'Skapa ett konto, klicka på "Lägg in annons" och fyll i uppgifterna. Det är helt gratis.',
  },
  {
    q: 'Vad är en förfrågan?',
    a: 'En förfrågan talar om vad du söker så att säljare kan kontakta dig direkt.',
  },
  {
    q: 'Hur vet jag vilken däckdimension jag behöver?',
    a: 'Dimensionen står på däckets sida (t.ex. 205/55 R16) eller i bilens instruktionsbok.',
  },
  {
    q: 'Vad är skillnaden mellan friktionsdäck och dubbdäck?',
    a: 'Friktionsdäck har mjukare gummiblandning; dubbdäck har metalldubbar för bättre grepp på is.',
  },
  {
    q: 'Hur kontrollerar jag mönsterdjupet?',
    a: 'Använd en mönsterdjupsmätare. Lagkrav är minst 1,6 mm, rekommenderat för vinter är 3+ mm.',
  },
  {
    q: 'Kan jag använda olika fälgar som inte sitter till min bil?',
    a: 'Fälgar måste matcha bultmönster, ET-tal och centrumhål. Dubbelkolla alltid med säljaren.',
  },
  {
    q: 'Vad betyder ET-tal?',
    a: 'ET-talet anger fälgens inpressning i mm — hur långt ut eller in hjulet sitter i förhållande till fälgens mitt.',
  },
  {
    q: 'Vad är kompletta hjul?',
    a: 'Kompletta hjul är däck monterade på fälg — klara att sättas på bilen direkt.',
  },
];

const MobileFAQ = () => {
  const [open, setOpen] = useState(null);
  return (
    <section className={css.faq}>
      <div className={css.sectionPad}>
        <span className={css.eyebrow}>Support</span>
        <h2 className={css.sectionHeading}>Vanliga frågor</h2>
        <div className={css.faqList}>
          {FAQS.map((item, i) => {
            const isOpen = open === i;
            return (
              <div key={item.q} className={css.faqItem}>
                <button
                  type="button"
                  className={css.faqTrigger}
                  onClick={() => setOpen(isOpen ? null : i)}
                  aria-expanded={isOpen}
                >
                  <span className={css.faqQ}>{item.q}</span>
                  <svg
                    className={`${css.faqChev}${isOpen ? ' ' + css.faqChevOpen : ''}`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div
                  className={`${css.faqAWrap}${isOpen ? ' ' + css.faqAWrapOpen : ''}`}
                  aria-hidden={!isOpen}
                >
                  <div className={css.faqA}>{item.a}</div>
                </div>
              </div>
            );
          })}
        </div>

        <div className={css.contactCard}>
          <h3 className={css.contactTitle}>Behöver du hjälp?</h3>
          <p className={css.contactCopy}>Vår oberoende kundsupport guidar dig rätt.</p>
          <NamedLink name="CMSPage" params={{ pageId: 'kontakt' }} className={css.contactCta}>
            Kontakta oss
          </NamedLink>
        </div>
      </div>
    </section>
  );
};

/* -------------------------- Root -------------------------- */

const WheelplaceLandingMobile = ({ FeaturedListingsComponent }) => (
  <div className={css.root}>
    {/* MobileHero is above the fold — render without Reveal. */}
    <MobileHero />
    <Reveal><MobileFeatured FeaturedListingsComponent={FeaturedListingsComponent} /></Reveal>
    <Reveal><MobileCategories /></Reveal>
    <Reveal><MobileBrands /></Reveal>
    <Reveal><HowItWorks /></Reveal>
    <Reveal><ProductEducation /></Reveal>
    <Reveal><MobileTrust /></Reveal>
    <Reveal><MobileFAQ /></Reveal>
  </div>
);

export default WheelplaceLandingMobile;
